import { translations } from '@/components/utils/translations';
import { capitalizeSentimentDisplay } from '@/utils/procurementDealUtils';

function normalizeKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function resolveTranslator(tOrLanguage) {
  if (typeof tOrLanguage === 'function') {
    return tOrLanguage;
  }
  const language = typeof tOrLanguage === 'string' && tOrLanguage ? tOrLanguage : 'en';
  return (key) => translations[language]?.[key] ?? translations.en[key] ?? key;
}

const BVO_TRANSLATION_KEYS = {
  budgetdriven: 'bvoBudgetDriven',
  valueseeker: 'bvoValueSeeker',
  outcomefocused: 'bvoOutcomeFocused',
  innovationdriven: 'bvoInnovationDriven',
  riskaverse: 'bvoRiskAverse',
  compliancefocused: 'bvoComplianceFocused',
};

const BVO_SUBSTRING_RULES = [
  { match: (norm) => norm.includes('budget'), key: 'bvoBudgetDriven' },
  { match: (norm) => norm.includes('value'), key: 'bvoValueSeeker' },
  { match: (norm) => norm.includes('outcome'), key: 'bvoOutcomeFocused' },
  { match: (norm) => norm.includes('innov'), key: 'bvoInnovationDriven' },
  { match: (norm) => norm.includes('risk'), key: 'bvoRiskAverse' },
  { match: (norm) => norm.includes('compli'), key: 'bvoComplianceFocused' },
];

const SENTIMENT_TRANSLATION_KEYS = {
  positive: 'sentimentPositive',
  pos: 'sentimentPositive',
  neutral: 'sentimentNeutral',
  neu: 'sentimentNeutral',
  negative: 'sentimentNegative',
  neg: 'sentimentNegative',
  satisfied: 'satisfied',
  frustrated: 'frustrated',
  exploringalternatives: 'exploringAlternatives',
  happy: 'satisfied',
  loyal: 'satisfied',
  committed: 'satisfied',
  dissatisfied: 'sentimentDissatisfied',
  atrisk: 'sentimentAtRisk',
  churning: 'frustrated',
  mixed: 'sentimentNeutral',
  unknown: 'unknown',
};

const PROCUREMENT_LABELS = ['Friendly', 'Standard', 'Bureaucratic', 'Monitor'];

const PROCUREMENT_TRANSLATION_KEYS = {
  friendly: 'procurementFriendlyLabel',
  standard: 'procurementStandardLabel',
  bureaucratic: 'procurementBureaucraticLabel',
  monitor: 'procurementMonitorLabel',
};

/** Map numeric procurement_friendliness scores to enum labels (matches admin data cleanup). */
export function coerceProcurementFriendliness(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number' || /^\d+(\.\d+)?$/.test(String(value).trim())) {
    const n = Number(value);
    if (n >= 80) return 'Friendly';
    if (n >= 60) return 'Standard';
    if (n >= 40) return 'Bureaucratic';
    return 'Monitor';
  }
  const raw = String(value).trim();
  const norm = normalizeKey(raw);
  const fromNorm = PROCUREMENT_LABELS.find((label) => normalizeKey(label) === norm);
  return fromNorm || raw;
}

export function translateBvoLabel(tOrLanguage, label) {
  const tr = resolveTranslator(tOrLanguage);
  const raw = String(label || '').trim();
  if (!raw) return '';

  const norm = normalizeKey(raw);
  const directKey = BVO_TRANSLATION_KEYS[norm];
  if (directKey) return tr(directKey);

  const substringKey = BVO_SUBSTRING_RULES.find((rule) => rule.match(norm))?.key;
  if (substringKey) return tr(substringKey);

  return raw;
}

export function translateIncumbentSentiment(tOrLanguage, sentiment) {
  const tr = resolveTranslator(tOrLanguage);
  const raw = String(sentiment || '').trim();
  if (!raw) return '';

  const norm = normalizeKey(raw);
  const key = SENTIMENT_TRANSLATION_KEYS[norm];
  if (key) return tr(key);
  if (norm.includes('exploring')) return tr('exploringAlternatives');

  return capitalizeSentimentDisplay(raw);
}

export function translateProcurementFriendliness(tOrLanguage, value) {
  const tr = resolveTranslator(tOrLanguage);
  const coerced = coerceProcurementFriendliness(value);
  if (!coerced) return '';

  const key = PROCUREMENT_TRANSLATION_KEYS[normalizeKey(coerced)];
  return key ? tr(key) : coerced;
}
